/**
 * The orders table.
 *
 * Eight columns in the order spec 02-orders lists them, sortable on the four the server
 * can sort, selectable for the bulk bar, and one contextual button per row. It is the
 * shared `DataTable`, so the responsive rule, the virtualiser past sixty rows, the keyboard
 * list navigation and the real table semantics all come for free and behave the same way
 * they do on Stock.
 *
 * Column priorities decide what drops on a narrower desktop: Items goes first, then Prints,
 * both into the row expander. Order, Customer, Payment and the action never drop, because
 * they are what a person needs to know which order this is and what to do about it.
 */
import { useMemo, type ReactNode } from 'react';
import type { OrderQueue } from '@elutide/shared';
import { DataTable, EmptyState, type Column, type DataTableSort } from '@/components';
import { definitionFor, type LegendIndex } from '@/lib/legend';
import {
  ActionCell,
  CustomerCell,
  FulfilmentCell,
  ItemsCell,
  OrderCell,
  PaymentCell,
  PrintsCell,
  TotalCell,
  type RowHandlers,
} from './cells';
import { QUEUE_LABELS, type OrdersPageRowView as OrdersPageRow } from './types';
import type { OrdersSort } from './searchParams';

/** Column id to legend id, so a header explains what the column holds. */
const COLUMN_LEGEND: Readonly<Record<string, string>> = {
  placed: 'orders.order',
  customer: 'orders.customer',
  payment: 'orders.payment',
  total: 'orders.total',
  items: 'orders.items',
  prints: 'orders.prints',
  state: 'orders.fulfilment',
};

export interface OrdersTableProps {
  rows: readonly OrdersPageRow[];
  queue: OrderQueue;
  legend: LegendIndex;
  sort: { by: OrdersSort; dir: 'asc' | 'desc' };
  onSortChange: (sort: DataTableSort) => void;
  selected: ReadonlySet<number>;
  onToggleSelected: (orderId: number) => void;
  onOpen: (row: OrdersPageRow) => void;
  handlers: RowHandlers;
  loading: boolean;
  /** Something is on the page but the current query found nothing. */
  emptyAction?: ReactNode;
}

export function OrdersTable({
  rows,
  queue,
  legend,
  sort,
  onSortChange,
  selected,
  onToggleSelected,
  onOpen,
  handlers,
  loading,
  emptyAction,
}: OrdersTableProps) {
  /**
   * The Total column exists only when the rows carry a total.
   *
   * A packer's rows never do (`redact.ts`), and a column of eight empty cells under a
   * "Total" header is a column that says the numbers were hidden. Decided from the rows
   * rather than the role so the screen and the response cannot disagree.
   */
  const showTotal = rows.length === 0 || rows.some((row) => row.total_cents !== undefined);

  const columns = useMemo<Column<OrdersPageRow>[]>(
    () => [
      {
        id: 'placed',
        header: 'Order',
        priority: 1,
        sortable: true,
        width: '17%',
        hint: definitionFor(legend, COLUMN_LEGEND.placed),
        render: (row) => <OrderCell row={row} now={handlers.now} />,
      },
      {
        id: 'customer',
        header: 'Customer',
        priority: 1,
        sortable: true,
        width: '15%',
        hint: definitionFor(legend, COLUMN_LEGEND.customer),
        render: (row) => <CustomerCell row={row} />,
      },
      {
        id: 'payment',
        header: 'Payment',
        priority: 1,
        width: '21%',
        hint: definitionFor(legend, COLUMN_LEGEND.payment),
        render: (row) => <PaymentCell row={row} handlers={handlers} />,
      },
      ...(showTotal
        ? [
            {
              id: 'total',
              header: 'Total',
              priority: 2,
              align: 'right',
              sortable: true,
              money: true,
              width: '10%',
              hint: definitionFor(legend, COLUMN_LEGEND.total),
              render: (row) => <TotalCell row={row} />,
            } satisfies Column<OrdersPageRow>,
          ]
        : []),
      {
        id: 'items',
        header: 'Items',
        priority: 5,
        align: 'right',
        width: '7%',
        hint: definitionFor(legend, COLUMN_LEGEND.items),
        render: (row) => <ItemsCell row={row} />,
      },
      {
        id: 'prints',
        header: 'Prints',
        priority: 4,
        width: '8%',
        hint: definitionFor(legend, COLUMN_LEGEND.prints),
        render: (row) => <PrintsCell row={row} />,
      },
      {
        id: 'state',
        header: 'Fulfilment',
        priority: 2,
        sortable: true,
        width: '14%',
        hint: definitionFor(legend, COLUMN_LEGEND.state),
        render: (row) => <FulfilmentCell row={row} />,
      },
      {
        id: 'action',
        header: 'Action',
        priority: 1,
        align: 'right',
        width: '11%',
        render: (row) => <ActionCell row={row} handlers={handlers} />,
      },
    ],
    [handlers, legend, showTotal],
  );

  return (
    <div className="ord-table">
      <DataTable<OrdersPageRow, number>
        columns={columns}
        rows={rows}
        rowKey={(row) => row.order_id}
        onRowClick={onOpen}
        selection={{ selected, onToggle: onToggleSelected }}
        sort={{ by: sort.by, dir: sort.dir }}
        onSortChange={onSortChange}
        loading={loading}
        caption={`Orders, ${QUEUE_LABELS[queue]}`}
        estimatedRowHeight={56}
        emptyState={
          <EmptyState
            message={`No orders in ${QUEUE_LABELS[queue]} for this search and range.`}
            action={emptyAction}
          />
        }
      />
    </div>
  );
}
